import { Switch } from '../ui/Form.js';
import { Icon } from '../ui/Icon.js';

/**
 * Opening hours as people describe them: a week, plus dated exceptions.
 *
 * Shared by Time schedules and an inbound route's hours of its own, so the two
 * cannot drift into two ways of saying "eight to five, closed on public
 * holidays". Underneath, both become the same schedule rules the switch
 * evaluates.
 */

export const DAYS = [
  { n: 1, label: 'Monday', short: 'Mon' },
  { n: 2, label: 'Tuesday', short: 'Tue' },
  { n: 3, label: 'Wednesday', short: 'Wed' },
  { n: 4, label: 'Thursday', short: 'Thu' },
  { n: 5, label: 'Friday', short: 'Fri' },
  { n: 6, label: 'Saturday', short: 'Sat' },
  { n: 0, label: 'Sunday', short: 'Sun' },
] as const;

/** Exceptions must beat the weekly pattern beneath them, hence the priority. */
export const EXCEPTION_PRIORITY = 100;

export interface DayDraft { open: boolean; from: string; to: string }
export interface ExceptionDraft {
  id: string; label: string; from: string; to: string; isOpen: boolean;
}
export interface HoursDraft { week: Record<number, DayDraft>; exceptions: ExceptionDraft[] }

/** A stored rule, as schedules and routes both return them. */
export interface RuleLike {
  id?: string;
  kind: 'weekly' | 'date_range' | 'date';
  days_of_week: number[] | null;
  time_from: string | null; time_to: string | null;
  date_from: string | null; date_to: string | null;
  is_open: boolean; priority: number; label: string | null;
}

/**
 * Postgres returns a `time` as HH:MM:SS. An <input type="time"> with the
 * default step only accepts HH:MM and silently renders EMPTY otherwise, so a
 * saved schedule would open with blank times and quietly save 00:00.
 */
export const hhmm = (t: string | null | undefined, fallback: string): string =>
  (t ?? fallback).slice(0, 5);

/**
 * Weekdays 08:00-17:00 open, the weekend closed: the shape of most South
 * African offices, and a starting point beats an empty grid.
 */
export const blankHours = (): HoursDraft => {
  const week: Record<number, DayDraft> = {};
  for (const d of DAYS) week[d.n] = { open: d.n >= 1 && d.n <= 5, from: '08:00', to: '17:00' };
  return { week, exceptions: [] };
};

export function hoursFromRules(rules: readonly RuleLike[]): HoursDraft {
  const week: Record<number, DayDraft> = {};
  for (const d of DAYS) week[d.n] = { open: false, from: '08:00', to: '17:00' };
  for (const r of rules) {
    if (r.kind !== 'weekly' || !r.is_open) continue;
    for (const d of r.days_of_week ?? []) {
      week[d] = { open: true, from: hhmm(r.time_from, '08:00'), to: hhmm(r.time_to, '17:00') };
    }
  }
  const exceptions = rules
    .filter((r) => r.kind !== 'weekly')
    .map((r) => ({
      id: r.id ?? crypto.randomUUID(),
      label: r.label ?? '',
      from: r.date_from ?? '',
      to: r.date_to ?? r.date_from ?? '',
      isOpen: r.is_open,
    }));
  return { week, exceptions };
}

/** The rules to save: one weekly rule per open day, then the exceptions above them. */
export function rulesFromHours(h: HoursDraft) {
  return [
    ...DAYS.filter((d) => h.week[d.n]?.open).map((d) => ({
      kind: 'weekly' as const,
      days_of_week: [d.n],
      time_from: h.week[d.n]!.from,
      time_to: h.week[d.n]!.to,
      is_open: true,
      priority: 0,
    })),
    ...h.exceptions
      .filter((x) => x.from)
      .map((x) => ({
        kind: (x.to && x.to !== x.from ? 'date_range' : 'date') as 'date_range' | 'date',
        date_from: x.from,
        ...(x.to && x.to !== x.from ? { date_to: x.to } : {}),
        is_open: x.isOpen,
        priority: EXCEPTION_PRIORITY,
        ...(x.label ? { label: x.label } : {}),
      })),
  ];
}

/** The first day that closes before it opens, which the rules cannot express as one row. */
export const invalidDay = (h: HoursDraft) =>
  DAYS.find((d) => h.week[d.n]!.open && h.week[d.n]!.from >= h.week[d.n]!.to);

export function HoursEditor({ value, onChange, idPrefix = 'hours' }: {
  value: HoursDraft; onChange: (next: HoursDraft) => void; idPrefix?: string;
}) {
  const setDay = (n: number, patch: Partial<DayDraft>) =>
    onChange({ ...value, week: { ...value.week, [n]: { ...value.week[n]!, ...patch } } });
  const setExceptions = (exceptions: ExceptionDraft[]) => onChange({ ...value, exceptions });
  const patchException = (id: string, patch: Partial<ExceptionDraft>) =>
    setExceptions(value.exceptions.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  /** Copies Monday's hours down. The alternative is typing 09:00 five times. */
  const copyMondayDown = () => {
    const mon = value.week[1]!;
    const week = { ...value.week };
    for (const d of DAYS) if (d.n >= 2 && d.n <= 5) week[d.n] = { ...mon };
    onChange({ ...value, week });
  };

  const bad = invalidDay(value);

  return (
    <>
      <div className="arc-field">
        <div className="arc-row" style={{ marginBottom: 7 }}>
          <span className="arc-label" style={{ margin: 0 }}>Open hours</span>
          <div className="arc-spacer" />
          <button type="button" className="arc-btn arc-btn-sm" onClick={copyMondayDown}>
            Copy Monday to Tue–Fri
          </button>
        </div>

        <div className="arc-week">
          {DAYS.map((d) => {
            const day = value.week[d.n]!;
            const wrong = day.open && day.from >= day.to;
            return (
              <div key={d.n} className="arc-week-row" data-open={day.open}>
                <div className="arc-week-day">
                  <Switch checked={day.open} label={`${d.label} open`}
                    onChange={(open) => setDay(d.n, { open })} />
                  <span>{d.label}</span>
                </div>
                {day.open ? (
                  <div className="arc-row" style={{ gap: 6 }}>
                    <input type="time" className="arc-input arc-time" value={day.from}
                      aria-label={`${d.label} opens`} aria-invalid={wrong}
                      onChange={(e) => setDay(d.n, { from: e.target.value })} />
                    <span className="arc-subtle">to</span>
                    <input type="time" className="arc-input arc-time" value={day.to}
                      aria-label={`${d.label} closes`} aria-invalid={wrong}
                      onChange={(e) => setDay(d.n, { to: e.target.value })} />
                  </div>
                ) : (
                  <span className="arc-subtle" style={{ fontSize: 12 }}>closed</span>
                )}
              </div>
            );
          })}
        </div>

        {bad && (
          <div className="arc-banner arc-banner-warn" style={{ marginTop: 9 }}>
            <Icon name="warning" size={14} />
            <span>
              {bad.label} closes before it opens. Overnight hours need two
              schedules — one for each side of midnight.
            </span>
          </div>
        )}
      </div>

      <div className="arc-field">
        <div className="arc-row" style={{ marginBottom: 7 }}>
          <span className="arc-label" style={{ margin: 0 }}>Holidays and exceptions</span>
          <div className="arc-spacer" />
          <button type="button" className="arc-btn arc-btn-sm"
            onClick={() => setExceptions([
              ...value.exceptions,
              { id: crypto.randomUUID(), label: '', from: '', to: '', isOpen: false },
            ])}>
            <Icon name="plus" size={12} /> Add date
          </button>
        </div>

        {value.exceptions.length === 0 ? (
          <p className="arc-muted" style={{ fontSize: 12.5 }}>
            None. Public holidays follow the weekly pattern unless listed here.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {value.exceptions.map((x, i) => (
              <div key={x.id} className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <input className="arc-input" style={{ maxWidth: 200 }} placeholder="Christmas Day"
                  id={`${idPrefix}-x${i}`}
                  value={x.label} aria-label={`Exception ${i + 1} label`}
                  onChange={(e) => patchException(x.id, { label: e.target.value })} />
                <input type="date" className="arc-input" style={{ maxWidth: 150 }}
                  value={x.from} aria-label={`Exception ${i + 1} from`}
                  onChange={(e) => patchException(x.id, { from: e.target.value })} />
                <span className="arc-subtle">to</span>
                <input type="date" className="arc-input" style={{ maxWidth: 150 }}
                  value={x.to} aria-label={`Exception ${i + 1} to`}
                  onChange={(e) => patchException(x.id, { to: e.target.value })} />
                <select className="arc-input" style={{ maxWidth: 120 }}
                  value={x.isOpen ? 'open' : 'closed'} aria-label={`Exception ${i + 1} state`}
                  onChange={(e) => patchException(x.id, { isOpen: e.target.value === 'open' })}>
                  <option value="closed">Closed</option>
                  <option value="open">Open</option>
                </select>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                  aria-label={`Remove exception ${i + 1}`}
                  onClick={() => setExceptions(value.exceptions.filter((y) => y.id !== x.id))}>
                  <Icon name="trash" size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
